import { useCallback, useEffect, useRef, useState } from 'react'
import { buildUserMessage, conversationIdOf, createAgentClient, type Activity } from '../agent'
import { composeAgentMessage, displayText, type AttachedDocument } from '../lib/documents'
import {
  deleteConversation,
  loadHistory,
  saveConversation,
  titleFor,
  type StoredConversation,
} from '../lib/history'

export type ChatRole = 'user' | 'agent'

export interface MessageAttachment {
  name: string
  path: string
  kind: AttachedDocument['kind']
  bytes: number
}

export interface ChatMessage {
  id: string
  role: ChatRole
  text: string
  timestamp: number
  attachments?: MessageAttachment[]
}

export type ChatStatus = 'connecting' | 'ready' | 'thinking' | 'error'

function newId(): string {
  return crypto.randomUUID()
}

function toMessage(role: ChatRole, text: string, attachments?: MessageAttachment[]): ChatMessage {
  return { id: newId(), role, text, timestamp: Date.now(), attachments }
}

function toAttachment(doc: AttachedDocument): MessageAttachment {
  return { name: doc.name, path: doc.path, kind: doc.kind, bytes: doc.bytes }
}

function suggestionsOf(activity: Activity): string[] {
  const actions = activity.suggestedActions?.actions ?? []
  return actions.map((action) => action.title ?? action.text ?? '').filter(Boolean)
}

/** `userKey` identifies the signed-in account; `null` keeps the hook idle. */
export function useChat(userKey: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [streamingText, setStreamingText] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [status, setStatus] = useState<ChatStatus>('connecting')
  const [error, setError] = useState<string | null>(null)
  const [history, setHistory] = useState<StoredConversation[]>([])
  const [activeId, setActiveId] = useState<string>(newId())

  const conversationId = useRef<string | undefined>(undefined)
  const started = useRef(false)

  const consume = useCallback(async (stream: AsyncGenerator<Activity>) => {
    let pending = ''
    for await (const activity of stream) {
      conversationId.current ??= conversationIdOf(activity)

      if (activity.type === 'typing') {
        if (activity.text) {
          pending = activity.text
          setStreamingText(pending)
        }
        continue
      }

      if (activity.type === 'message' && activity.text) {
        pending = ''
        setStreamingText('')
        setMessages((prev) => [...prev, toMessage('agent', activity.text!)])
        setSuggestions(suggestionsOf(activity))
      }
    }
    // A stream can end while a partial reply is still on screen (e.g. the agent
    // only ever emitted typing activities) — promote it so nothing is lost.
    if (pending) {
      setStreamingText('')
      setMessages((prev) => [...prev, toMessage('agent', pending)])
    }
  }, [])

  const start = useCallback(async () => {
    setStatus('connecting')
    setError(null)
    try {
      const client = await createAgentClient()
      await consume(client.startConversationStreaming(true))
      setStatus('ready')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to reach the agent.')
      setStatus('error')
    }
  }, [consume])

  useEffect(() => {
    if (!userKey || started.current) return
    started.current = true
    void start()
  }, [userKey, start])

  useEffect(() => {
    if (!userKey) return
    setHistory(loadHistory(userKey))
  }, [userKey])

  // Persist only once the user has actually said something, so an unused chat
  // (agent greeting alone) never clutters the history list.
  useEffect(() => {
    if (!userKey || !messages.some((message) => message.role === 'user')) return
    setHistory(
      saveConversation(userKey, {
        id: activeId,
        title: titleFor(messages),
        updatedAt: Date.now(),
        conversationId: conversationId.current,
        messages,
      }),
    )
  }, [userKey, activeId, messages])

  const send = useCallback(
    async (text: string, documents: AttachedDocument[] = []) => {
      const shown = displayText(text, documents)
      if (!shown || status === 'thinking' || status === 'connecting') return

      const payload = composeAgentMessage(text, documents)
      const attachments = documents.length > 0 ? documents.map(toAttachment) : undefined

      setMessages((prev) => [...prev, toMessage('user', shown, attachments)])
      setSuggestions([])
      setStatus('thinking')
      setError(null)

      try {
        const client = await createAgentClient()
        await consume(client.sendActivityStreaming(buildUserMessage(payload), conversationId.current))
        setStatus('ready')
      } catch (err) {
        setStreamingText('')
        setError(err instanceof Error ? err.message : 'The agent did not respond.')
        setStatus('error')
      }
    },
    [consume, status],
  )

  const reset = useCallback(() => {
    conversationId.current = undefined
    setActiveId(newId())
    setMessages([])
    setStreamingText('')
    setSuggestions([])
    void start()
  }, [start])

  const openConversation = useCallback(
    (id: string) => {
      const stored = history.find((item) => item.id === id)
      if (!stored) return
      // Reuse the stored Copilot Studio conversation id when it is still valid so
      // follow-up questions land in the same server-side thread.
      conversationId.current = stored.conversationId
      setActiveId(stored.id)
      setMessages(stored.messages)
      setStreamingText('')
      setSuggestions([])
      setError(null)
      setStatus('ready')
    },
    [history],
  )

  const removeConversation = useCallback(
    (id: string) => {
      if (!userKey) return
      setHistory(deleteConversation(userKey, id))
      if (id === activeId) reset()
    },
    [userKey, activeId, reset],
  )

  return {
    messages,
    streamingText,
    suggestions,
    status,
    error,
    history,
    activeId,
    send,
    reset,
    openConversation,
    removeConversation,
    retry: start,
  }
}
